import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { digitalOcean } from "@/integrations/digitalocean/client";
import { useQuery } from "@tanstack/react-query";
import { createPageUrl } from "@/utils";
import { Trophy, Heart, Play, Pause, Vote, Search, Music } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { usePlayer } from "@/contexts/PlayerContext";
import { hapticMedium, hapticSuccess, hapticError, hapticLight } from "@/utils/haptic";
import { CountdownTimer } from "@/components/CountdownTimer";
import { WinnerBadge } from "@/components/WinnerBadge";
import { LeaderboardBadge } from "@/components/LeaderboardBadge";
import { useLeaderboardPosition } from "@/hooks/useLeaderboardPosition";
import { getDefaultCoverImage } from "@/utils/defaultCoverImage";

// Helper component to show leaderboard badge for an artist
function ArtistLeaderboardBadge({ artistId }) {
  const { data: position } = useLeaderboardPosition(artistId);
  return position ? <LeaderboardBadge position={position} /> : null;
}

export default function AnnualLeaderboard() {
  const navigate = useNavigate();
  const { playTrack, currentTrack, isPlaying, togglePlayPause } = usePlayer();
  const [currentUser, setCurrentUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [voteDialogOpen, setVoteDialogOpen] = useState(false);
  const [selectedTrackId, setSelectedTrackId] = useState(null);
  const [voteAmount, setVoteAmount] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const tracksPerPage = 100;

  // Check if we're in December (voting is open)
  const now = new Date();
  const isDecember = now.getMonth() === 11; // 11 = December
  const voteMultiplier = isDecember ? 3 : 1;

  useEffect(() => {
    document.title = "Annual Leaderboard - Soundope";
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      if (session) {
        const { data: profile } = await digitalOcean
          .from("profiles")
          .select("*")
          .eq("id", session.user.id)
          .single();
        setCurrentUser(profile);
      }
    } catch (error) {
      console.error("Error loading user:", error);
    }
  };

  // Get top 10 tracks from the monthly leaderboard (same data, top performers)
  const { data: annualTracks = [], isLoading, refetch } = useQuery({
    queryKey: ["annual-leaderboard-tracks"],
    queryFn: async () => {
      const { data: tracks, error } = await digitalOcean
        .from("tracks")
        .select("*")
        .eq("is_active", true)
        .order("praise_count", { ascending: false })
        .limit(10);

      if (error) throw error;

      // Transform to include annual_votes (same as praise_count for now)
      return tracks.map(track => ({
        ...track,
        annual_votes: track.praise_count
      }));
    },
  });

  useEffect(() => {
    if (annualTracks.length > 0 && !currentTrack) {
      playTrack(annualTracks[0], annualTracks, 0, true);
    }
  }, [annualTracks, playTrack, currentTrack]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const filteredTracks = annualTracks.filter(track =>
    track.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    track.artist_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filteredTracks.length / tracksPerPage);
  const startIndex = (currentPage - 1) * tracksPerPage;
  const paginatedTracks = filteredTracks.slice(startIndex, startIndex + tracksPerPage);

  const openVoteDialog = (trackId) => {
    if (!isDecember) {
      hapticError();
      toast.error("Voting is only available in December!");
      return;
    }

    if (!currentUser) {
      hapticError();
      toast.error("Please sign in to vote");
      navigate(createPageUrl("Auth"));
      return;
    }

    hapticLight();
    setSelectedTrackId(trackId);
    setVoteAmount("");
    setVoteDialogOpen(true);
  };

  const handleVote = async () => {
    if (!isDecember) {
      hapticError();
      toast.error("Voting is only available in December!");
      return;
    }

    const votes = parseInt(voteAmount);
    
    if (!votes || votes <= 0) {
      hapticError();
      toast.error("Please enter a valid number of votes");
      return;
    }

    // Calculate effective votes (3x multiplier in December)
    const effectiveVotes = votes * voteMultiplier;
    
    // Check if user has enough weekly votes (we still deduct normal amount)
    if (votes > currentUser.weekly_votes_remaining) {
      hapticError();
      toast.error(`You only have ${currentUser.weekly_votes_remaining} votes remaining`);
      return;
    }

    hapticMedium();

    try {
      // Insert vote record
      const { error: voteError } = await digitalOcean
        .from("votes")
        .insert({
          track_id: selectedTrackId,
          voter_id: currentUser.id,
          vote_type: "praise"
        });

      if (voteError) throw voteError;

      // Update annual leaderboard total_votes with effective votes (3x)
      const track = annualTracks.find(t => t.id === selectedTrackId);
      const currentAnnualVotes = track?.annual_votes || 0;
      
      const { error: annualUpdateError } = await digitalOcean
        .from("annual_leaderboard")
        .update({ total_votes: currentAnnualVotes + effectiveVotes })
        .eq("track_id", selectedTrackId)
        .eq("year", now.getFullYear());

      if (annualUpdateError) throw annualUpdateError;

      // Also update the track's regular praise_count (for consistency)
      const { error: trackUpdateError } = await digitalOcean
        .from("tracks")
        .update({ praise_count: (track?.praise_count || 0) + votes })
        .eq("id", selectedTrackId);

      if (trackUpdateError) throw trackUpdateError;

      // Decrement weekly votes (only deduct normal amount, not 3x)
      const { error: voteDecrementError } = await digitalOcean
        .from("profiles")
        .update({ weekly_votes_remaining: currentUser.weekly_votes_remaining - votes })
        .eq("id", currentUser.id);

      if (voteDecrementError) throw voteDecrementError;

      // Update local state
      setCurrentUser({ ...currentUser, weekly_votes_remaining: currentUser.weekly_votes_remaining - votes });

      hapticSuccess();
      toast.success(`${votes} votes cast (worth ${effectiveVotes} on annual leaderboard)!`);
      setVoteDialogOpen(false);
      refetch();
    } catch (error) {
      console.error("Vote error:", error);
      hapticError();
      toast.error("Failed to cast vote");
    }
  };

  const handlePlayTrack = (track, globalIndex) => {
    if (currentTrack && currentTrack.id === track.id) {
      togglePlayPause();
      return;
    }

    playTrack(track, filteredTracks, globalIndex, true);
    toast.success(`Now playing: ${track.title}`);
  };

  const getMedalIcon = (rank) => {
    if (rank === 0) return <Trophy className="w-6 h-6 text-yellow-500" />;
    if (rank === 1) return <Trophy className="w-6 h-6 text-gray-400" />;
    if (rank === 2) return <Trophy className="w-6 h-6 text-amber-600" />;
    return <span className="w-6 h-6 flex items-center justify-center font-medium text-[#808080]">#{rank + 1}</span>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#808080]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 py-8 pb-32">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-4 mb-4">
            <h1 className="text-3xl md:text-4xl font-light text-[#d0d0d0]">
              Annual Leaderboard {now.getFullYear()}
            </h1>
            <Button
              onClick={() => navigate(createPageUrl("Leaderboard"))}
              className="neuro-base active:neuro-pressed rounded-xl px-4 py-2 text-sm"
            >
              <span className="text-[#a0a0a0]">Monthly</span>
            </Button>
          </div>
          <p className="text-sm md:text-base text-[#808080] mb-6">
            Tracks that won #1 in their month compete for the annual crown
          </p>
          
          {/* Countdown Timer */}
          <div className="mb-8 neuro-base rounded-2xl p-4 md:p-6">
            <CountdownTimer />
          </div>

          {!isDecember && (
            <div className="mb-6 p-4 rounded-2xl bg-[#1a1a1a] border border-[#2a2a2a]">
              <p className="text-sm md:text-base text-[#a0a0a0]">
                Voting opens December 1st. During December, votes are worth <strong className="text-[#10B981]">3x</strong>!
              </p>
            </div>
          )}

          {isDecember && (
            <div className="mb-6 p-4 rounded-2xl bg-[#10B981]/20 border-2 border-[#10B981]">
              <p className="text-sm md:text-base text-[#10B981] font-medium">
                🎉 Voting is OPEN! All votes are worth <strong>3x</strong> this month!
              </p>
            </div>
          )}
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#808080]" />
            <Input
              type="text"
              placeholder="Search tracks or artists..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 neuro-pressed rounded-2xl bg-[#0a0a0a] border-none text-[#d0d0d0]"
            />
          </div>
        </div>

        {/* Tracks List */}
        {filteredTracks.length === 0 ? (
          <div className="text-center py-12">
            <Music className="w-16 h-16 mx-auto text-[#606060] mb-4" />
            <p className="text-[#808080]">No tracks on the annual leaderboard yet</p>
          </div>
        ) : (
          <>
            <div className="space-y-4 mb-6">
              {paginatedTracks.map((track, index) => {
                const globalIndex = startIndex + index;
                const rank = globalIndex;
                const isCurrentlyPlaying = currentTrack?.id === track.id;

                return (
                  <div
                    key={track.id}
                    className="neuro-base rounded-2xl p-4 md:p-6 flex items-center gap-4 md:gap-6 group"
                  >
                    <div className="flex items-center gap-4 md:gap-6 flex-1 min-w-0">
                      {/* Rank */}
                      <div className="flex-shrink-0 w-12 md:w-16 text-center">
                        {getMedalIcon(rank)}
                      </div>

                      {/* Track Image */}
                      <div className="relative flex-shrink-0">
                        <img
                          src={track.cover_image_url || getDefaultCoverImage(track.id)}
                          alt={track.title}
                          className="w-20 h-20 rounded-xl object-cover"
                        />
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => handlePlayTrack(track, globalIndex)}
                                className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl"
                              >
                                {isCurrentlyPlaying && isPlaying ? (
                                  <Pause className="w-8 h-8 text-white" />
                                ) : (
                                  <Play className="w-8 h-8 text-white" />
                                )}
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{isCurrentlyPlaying && isPlaying ? "Pause" : "Play"}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>

                      {/* Track Info */}
                      <div className="flex-1 min-w-0 text-center md:text-left">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => navigate(createPageUrl("TrackDetails") + `?id=${track.id}`)}
                                className="text-left w-full"
                              >
                                <h3 className="text-lg md:text-2xl font-medium text-[#d0d0d0] mb-1 truncate hover:text-[#a0a0a0] transition-colors break-words">
                                  {track.title}
                                </h3>
                                <p className="text-base md:text-lg text-[#808080] mb-2 flex items-center gap-1.5">
                                  {track.artist_name}
                                  {track.artist_id && <ArtistLeaderboardBadge artistId={track.artist_id} />}
                                </p>
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>View track details and feedback</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        {track.month_won && (
                          <p className="text-xs text-[#707070] mb-1">
                            Won #1 in {new Date(2000, track.month_won - 1).toLocaleString('default', { month: 'long' })}
                          </p>
                        )}
                      </div>

                      {/* Votes and Actions */}
                      <div className="flex items-center gap-4 md:gap-6">
                        <div className="text-center">
                          <div className="flex items-center gap-2 mb-1">
                            <Heart className="w-4 h-4 text-[#a08080]" />
                            <span className="text-base md:text-xl font-light text-[#d0d0d0]">{track.annual_votes || 0}</span>
                          </div>
                          <p className="text-xs text-[#707070]">Annual Votes</p>
                        </div>
                        
                        <Button
                          onClick={() => openVoteDialog(track.id)}
                          disabled={!currentUser || !isDecember}
                          className="neuro-base active:neuro-pressed rounded-2xl px-4 md:px-6 py-2 md:py-3 flex items-center gap-2 text-sm md:text-base disabled:opacity-50"
                        >
                          <Vote className="w-4 h-4 text-[#a0a0a0]" />
                          <span className="text-[#a0a0a0]">Vote</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2">
                <Button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="neuro-base active:neuro-pressed rounded-xl px-4 py-2 disabled:opacity-50"
                >
                  Previous
                </Button>
                <span className="text-sm text-[#808080]">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="neuro-base active:neuro-pressed rounded-xl px-4 py-2 disabled:opacity-50"
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Vote Dialog */}
      <Dialog open={voteDialogOpen} onOpenChange={setVoteDialogOpen}>
        <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] neuro-base rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-[#d0d0d0]">
              Cast Your Votes {isDecember && <span className="text-[#10B981]">(3x Multiplier Active!)</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#808080]">
              You have {currentUser?.weekly_votes_remaining || 0} votes remaining
            </p>
            {isDecember && (
              <p className="text-sm text-[#10B981] font-medium">
                Each vote counts as 3 votes on the annual leaderboard!
              </p>
            )}
            <Input
              type="number"
              min="1"
              max={currentUser?.weekly_votes_remaining || 0}
              placeholder="How many votes?"
              value={voteAmount}
              onChange={(e) => setVoteAmount(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleVote()}
              className="bg-[#0a0a0a] text-[#d0d0d0] border-[#2a2a2a] neuro-pressed rounded-2xl"
            />
            <Button
              onClick={handleVote}
              className="w-full neuro-base active:neuro-pressed rounded-2xl px-6 py-3"
            >
              <span className="text-[#a0a0a0]">
                Cast {voteAmount ? `${voteAmount} vote${parseInt(voteAmount) !== 1 ? 's' : ''}` : 'Votes'}
                {isDecember && voteAmount && ` (${parseInt(voteAmount) * 3} on leaderboard)`}
              </span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

